import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, message, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet,
  ActionRow, DetailRow, Field, TextInput, TextAreaInput, SelectInput,
  Pill, Mono, Ico, fmt, useMobileMenu,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useLogisticsPermissions } from "../../gestion-logistics/hooks/useLogisticsPermissions";
import useShipmentWarehouse from "../../gestion-logistics/hooks/useShipmentWarehouse";
import {
  deleteLogisticsShipment, fetchLogisticsShipment, fetchLogisticsShipments, saveLogisticsShipment,
} from "./logisticsMobileApi";

const PAGE = 20;
const STATUS = {
  draft: { label: "Brouillon", tone: "off" }, confirmed: { label: "Confirmé", tone: "info" },
  in_transit: { label: "En transit", tone: "warn" }, delivered: { label: "Livré", tone: "ok" },
  completed: { label: "Terminé", tone: "ok" }, cancelled: { label: "Annulé", tone: "danger" },
};
const MODES = [
  { value: "sea", label: "Maritime" }, { value: "air", label: "Aérien" },
  { value: "road", label: "Routier" }, { value: "rail", label: "Ferroviaire" },
  { value: "multimodal", label: "Multimodal" },
];
const emptyForm = () => ({
  client_name: "", reference_client: "", status: "draft", transport_mode: "sea", service_type: "",
  consignee_name: "", shipper_name: "", receiver_name: "", receiver_address: "",
  origin_country: "", origin_city: "", origin_address: "", port_of_loading: "",
  destination_country: "", destination_city: "", destination_address: "", port_of_discharge: "", final_destination: "",
  goods_description: "", total_weight: "", total_volume: "", number_of_packages: "", incoterm: "",
  booking_date: "", etd: "", eta: "", bl_number: "", container_numbers: "",
  carrier_name: "", vessel_name: "", voyage_number: "", clearing_agent_name: "", internal_notes: "",
});
const formFrom = (row) => {
  const result = emptyForm();
  Object.keys(result).forEach((key) => {
    const value = row?.[key];
    if (value == null) result[key] = "";
    else result[key] = key.includes("date") || ["etd", "eta"].includes(key)
      ? String(value).slice(0, 10)
      : String(value);
  });
  return result;
};
const errorMessage = (error, fallback) => error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
const dateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("fr-FR");
};
const money = (value, symbol = "") => `${fmt(Number(value) || 0)}${symbol ? ` ${symbol}` : ""}`;

export default function ShipmentsMobile() {
  const onMenu = useMobileMenu();
  const navigate = useNavigate();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const permissions = useLogisticsPermissions();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [mode, setMode] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const warehouse = useShipmentWarehouse({ open: formOpen, shipment: editing, companyId: selectedCompany, selectedWarehouse });
  const draft = useRef(form);
  draft.current = form;
  const selection = `${selectedCompany}:${selectedWarehouse}`;
  const selectionState = useRef({ key: selection, version: 0 });
  if (selectionState.current.key !== selection) selectionState.current = { key: selection, version: selectionState.current.version + 1 };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  const load = useCallback(async (page = 1, append = false) => {
    if (!selectedCompany || !permissions.canViewShipments) return;
    setLoading(true);
    try {
      const filters = {};
      if (debouncedQuery) filters.search = debouncedQuery;
      if (status !== "all") filters.status = status;
      if (mode !== "all") filters.transport_mode = mode;
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;
      const result = await fetchLogisticsShipments({ companyId: selectedCompany, page, limit: PAGE, ...filters });
      setRows((previous) => append ? [...previous, ...result.rows] : result.rows);
      setPagination(result.pagination);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les dossiers"));
      if (!append) setRows([]);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, debouncedQuery, mode, permissions.canViewShipments, selectedCompany, status]);

  useEffect(() => { load(1, false); }, [load]);

  const stats = useMemo(() => ({
    active: rows.filter((row) => ["confirmed", "in_transit", "delivered"].includes(row.status)).length,
    transit: rows.filter((row) => row.status === "in_transit").length,
  }), [rows]);

  const openDetail = async (row) => {
    setDetail(row);
    setDetailLoading(true);
    try { setDetail(await fetchLogisticsShipment(row.id) || row); }
    catch (error) { message.error(errorMessage(error, "Impossible de charger la fiche")); }
    finally { setDetailLoading(false); }
  };

  const openForm = async (row = null) => {
    const version = selectionState.current.version;
    setDetail(null);
    if (!row) {
      setEditing(null);
      setForm(emptyForm());
      setFormOpen(true);
      return;
    }
    try {
      const full = await fetchLogisticsShipment(row.id) || row;
      if (version !== selectionState.current.version) return;
      setEditing(full);
      setForm(formFrom(full));
      setFormOpen(true);
    } catch (error) { message.error(errorMessage(error, "Impossible de préparer la modification")); }
  };

  const submit = async () => {
    if (inFlight.current || !form.client_name.trim() || !form.transport_mode) return;
    inFlight.current = true;
    setSaving(true);
    const isCurrent = warehouse.captureContext();
    try {
      const submittedDraft = draft.current;
      const warehouseFields = await warehouse.verify();
      if (draft.current !== submittedDraft) throw new Error("La saisie a changé pendant la vérification. Vérifiez puis réessayez.");
      const payload = { ...form, company_id: selectedCompany, ...warehouseFields };
      ["total_weight", "total_volume", "number_of_packages"].forEach((key) => { payload[key] = form[key] === "" ? null : Number(form[key]); });
      ["booking_date", "etd", "eta"].forEach((key) => { payload[key] = form[key] || null; });
      if (editing?.client_id) payload.client_id = editing.client_id;
      if (editing?.currency_id) payload.currency_id = editing.currency_id;
      const result = await saveLogisticsShipment(editing?.id, payload);
      if (result?.success === false) throw new Error(result.message || "Enregistrement impossible");
      await warehouse.saved();
      if (!isCurrent()) return;
      message.success(editing ? "Dossier mis à jour" : "Dossier créé");
      setFormOpen(false);
      setEditing(null);
      await load(1, false);
    } catch (error) { if (isCurrent()) message.error(errorMessage(error, "Impossible d'enregistrer le dossier")); }
    finally { inFlight.current = false; setSaving(false); }
  };

  const remove = (row) => Modal.confirm({
    title: "Supprimer ce dossier ?", content: row.shipment_number, okText: "Supprimer", okType: "danger", cancelText: "Annuler", centered: true,
    onOk: async () => {
      try { await deleteLogisticsShipment(row.id, selectedCompany); message.success("Dossier supprimé"); setDetail(null); await load(1, false); }
      catch (error) { message.error(errorMessage(error, "Suppression impossible")); }
    },
  });

  if (!permissions.canViewShipments) return <ProductScreen><Appbar title="Dossiers logistiques" onMenu={onMenu} /><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" /></div></ProductScreen>;
  if (!selectedCompany) return <ProductScreen><Appbar title="Dossiers logistiques" onMenu={onMenu} /><div className="body"><EmptyState icon="store" title="Entreprise requise" text="Sélectionnez une entreprise." /></div></ProductScreen>;

  return (
    <ProductScreen>
      <Appbar title="Dossiers logistiques" subtitle={`${fmt(pagination.total)} dossier${pagination.total > 1 ? "s" : ""}`} onMenu={onMenu} action={<button className="iconbtn" aria-label="Filtres" onClick={() => setFiltersOpen(true)}><Ico name="sliders-horizontal" /></button>} />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: "Total", value: fmt(pagination.total) }, { label: "Actifs chargés", value: fmt(stats.active) }, { label: "En transit", value: fmt(stats.transit) }]} />
        <SearchBar value={query} onChange={setQuery} placeholder="N° dossier, client, BL, trajet…" />
        <Chips value={status} onChange={setStatus} options={[{ key: "all", label: "Tous" }, { key: "draft", label: "Brouillons" }, { key: "confirmed", label: "Confirmés" }, { key: "in_transit", label: "En transit" }, { key: "delivered", label: "Livrés" }, { key: "completed", label: "Terminés" }, { key: "cancelled", label: "Annulés" }]} />
      </div>
      <div className="body">
        {loading && !rows.length ? <EmptyState loading title="Chargement…" /> : !rows.length ? <EmptyState icon="container" title="Aucun dossier" /> : <div className="list">
          {rows.map((row) => { const meta = STATUS[row.status] || { label: row.status, tone: "off" }; const modeLabel = MODES.find((item) => item.value === row.transport_mode)?.label || row.transport_mode; return <button key={row.id} className="rcard" onClick={() => openDetail(row)}>
            <Mono name={row.shipment_number} square /><div className="rcard__main"><div className="rcard__name">{row.shipment_number}</div><div className="rcard__meta"><Pill tone={meta.tone} label={meta.label} /><span className="rcard__sub">{row.client_name} · {modeLabel}</span></div></div><span className="rcard__chev"><Ico name="chevron-right" /></span>
          </button>; })}
          {rows.length < Number(pagination.total || 0) ? <button className="btn btn--ghost" onClick={() => load(Number(pagination.page || 1) + 1, true)} disabled={loading}>{loading ? "Chargement…" : `Charger plus (${rows.length}/${pagination.total})`}</button> : null}
        </div>}
      </div>
      {permissions.canCreateShipments ? <Fab label="Nouveau dossier" onClick={() => openForm()} /> : null}

      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filtres dossiers">
        <Field label="Mode de transport"><SelectInput value={mode} onChange={(value) => setMode(value || "all")} options={[{ value: "all", label: "Tous les modes" }, ...MODES]} /></Field>
        <Field label="Date de début"><div className="ctrl"><input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></div></Field>
        <Field label="Date de fin"><div className="ctrl"><input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></div></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => { setMode("all"); setStatus("all"); setQuery(""); setDateFrom(""); setDateTo(""); }}>Réinitialiser</button><button className="btn btn--primary" onClick={() => setFiltersOpen(false)}>Appliquer</button></div>
      </Sheet>

      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.shipment_number} subtitle={detailLoading ? "Chargement…" : detail?.client_name}>
        {detail ? <>
          <DetailRow label="Statut" value={STATUS[detail.status]?.label || detail.status} /><DetailRow label="Référence client" value={detail.reference_client || "—"} />
          <DetailRow label="Transport" value={MODES.find((item) => item.value === detail.transport_mode)?.label || detail.transport_mode} />
          <DetailRow label="Trajet" value={`${[detail.origin_city, detail.origin_country].filter(Boolean).join(", ") || "—"} → ${[detail.destination_city, detail.destination_country].filter(Boolean).join(", ") || "—"}`} />
          <DetailRow label="ETD / ETA" value={`${dateLabel(detail.etd)} → ${dateLabel(detail.eta)}`} /><DetailRow label="N° BL" value={detail.bl_number || "—"} />
          <DetailRow label="Marchandise" value={detail.goods_description || "—"} /><DetailRow label="Poids / Volume" value={`${detail.total_weight || 0} kg · ${detail.total_volume || 0} m³`} />
          <DetailRow label="Coûts" value={money(detail.total_cost, detail.currency_symbol)} /><DetailRow label="Revenus" value={money(detail.total_revenue, detail.currency_symbol)} /><DetailRow label="Marge" value={money(detail.profit_margin, detail.currency_symbol)} />
          <ActionRow icon="external-link" label="Ouvrir la fiche complète" onClick={() => navigate(`/logistics/shipment/${detail.id}`)} />
          {permissions.canUpdateShipmentStatus ? <ActionRow icon="pencil" label="Modifier" onClick={() => openForm(detail)} /> : null}
          {permissions.canDeleteShipments && detail.status === "draft" ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}
        </> : null}
      </Sheet>

      <Sheet open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Modifier le dossier" : "Nouveau dossier"}>
        {warehouse.error && <Alert type="error" showIcon message={warehouse.error}
          action={<Button onClick={() => { void warehouse.refresh().catch(() => {}); }}>Réessayer</Button>} style={{ marginBottom: 16 }} />}
        <Field label="Client *"><TextInput value={form.client_name} onChange={(client_name) => setForm((f) => ({ ...f, client_name }))} autoFocus /></Field>
        <Field label="Référence client"><TextInput value={form.reference_client} onChange={(reference_client) => setForm((f) => ({ ...f, reference_client }))} /></Field>
        <Field label="Statut"><SelectInput value={form.status} onChange={(statusValue) => setForm((f) => ({ ...f, status: statusValue }))} options={Object.entries(STATUS).map(([value, item]) => ({ value, label: item.label }))} /></Field>
        <Field label="Mode de transport *"><SelectInput value={form.transport_mode} onChange={(transport_mode) => setForm((f) => ({ ...f, transport_mode }))} options={MODES} /></Field>
        <Field label="Type de service"><TextInput value={form.service_type} onChange={(service_type) => setForm((f) => ({ ...f, service_type }))} /></Field>
        <Field label="Expéditeur"><TextInput value={form.shipper_name} onChange={(shipper_name) => setForm((f) => ({ ...f, shipper_name }))} /></Field>
        <Field label="Consignataire"><TextInput value={form.consignee_name} onChange={(consignee_name) => setForm((f) => ({ ...f, consignee_name }))} /></Field>
        <Field label="Destinataire"><TextInput value={form.receiver_name} onChange={(receiver_name) => setForm((f) => ({ ...f, receiver_name }))} /></Field>
        <Field label="Adresse destinataire"><TextAreaInput value={form.receiver_address} onChange={(receiver_address) => setForm((f) => ({ ...f, receiver_address }))} rows={2} /></Field>
        <Field label="Pays d'origine"><TextInput value={form.origin_country} onChange={(origin_country) => setForm((f) => ({ ...f, origin_country }))} /></Field>
        <Field label="Ville d'origine"><TextInput value={form.origin_city} onChange={(origin_city) => setForm((f) => ({ ...f, origin_city }))} /></Field>
        <Field label="Adresse d'origine"><TextAreaInput value={form.origin_address} onChange={(origin_address) => setForm((f) => ({ ...f, origin_address }))} rows={2} /></Field>
        <Field label="Port de chargement"><TextInput value={form.port_of_loading} onChange={(port_of_loading) => setForm((f) => ({ ...f, port_of_loading }))} /></Field>
        <Field label="Pays de destination"><TextInput value={form.destination_country} onChange={(destination_country) => setForm((f) => ({ ...f, destination_country }))} /></Field>
        <Field label="Ville de destination"><TextInput value={form.destination_city} onChange={(destination_city) => setForm((f) => ({ ...f, destination_city }))} /></Field>
        <Field label="Adresse de destination"><TextAreaInput value={form.destination_address} onChange={(destination_address) => setForm((f) => ({ ...f, destination_address }))} rows={2} /></Field>
        <Field label="Port de déchargement"><TextInput value={form.port_of_discharge} onChange={(port_of_discharge) => setForm((f) => ({ ...f, port_of_discharge }))} /></Field>
        <Field label="Destination finale"><TextInput value={form.final_destination} onChange={(final_destination) => setForm((f) => ({ ...f, final_destination }))} /></Field>
        <Field label="Description marchandise"><TextAreaInput value={form.goods_description} onChange={(goods_description) => setForm((f) => ({ ...f, goods_description }))} rows={3} /></Field>
        <Field label="Poids total (kg)"><TextInput value={form.total_weight} onChange={(total_weight) => setForm((f) => ({ ...f, total_weight }))} inputMode="decimal" /></Field>
        <Field label="Volume total (m³)"><TextInput value={form.total_volume} onChange={(total_volume) => setForm((f) => ({ ...f, total_volume }))} inputMode="decimal" /></Field>
        <Field label="Nombre de colis"><TextInput value={form.number_of_packages} onChange={(number_of_packages) => setForm((f) => ({ ...f, number_of_packages }))} inputMode="numeric" /></Field>
        <Field label="Incoterm"><TextInput value={form.incoterm} onChange={(incoterm) => setForm((f) => ({ ...f, incoterm }))} /></Field>
        <Field label="Date de réservation"><div className="ctrl"><input type="date" value={form.booking_date} onChange={(e) => setForm((f) => ({ ...f, booking_date: e.target.value }))} /></div></Field>
        <Field label="ETD"><div className="ctrl"><input type="date" value={form.etd} onChange={(e) => setForm((f) => ({ ...f, etd: e.target.value }))} /></div></Field>
        <Field label="ETA"><div className="ctrl"><input type="date" value={form.eta} onChange={(e) => setForm((f) => ({ ...f, eta: e.target.value }))} /></div></Field>
        <Field label="N° BL"><TextInput value={form.bl_number} onChange={(bl_number) => setForm((f) => ({ ...f, bl_number }))} /></Field>
        <Field label="N° conteneurs"><TextInput value={form.container_numbers} onChange={(container_numbers) => setForm((f) => ({ ...f, container_numbers }))} /></Field>
        <Field label="Transporteur"><TextInput value={form.carrier_name} onChange={(carrier_name) => setForm((f) => ({ ...f, carrier_name }))} /></Field>
        <Field label="Navire"><TextInput value={form.vessel_name} onChange={(vessel_name) => setForm((f) => ({ ...f, vessel_name }))} /></Field>
        <Field label="N° voyage"><TextInput value={form.voyage_number} onChange={(voyage_number) => setForm((f) => ({ ...f, voyage_number }))} /></Field>
        <Field label="Agent de dédouanement"><TextInput value={form.clearing_agent_name} onChange={(clearing_agent_name) => setForm((f) => ({ ...f, clearing_agent_name }))} /></Field>
        <Field label="Notes internes"><TextAreaInput value={form.internal_notes} onChange={(internal_notes) => setForm((f) => ({ ...f, internal_notes }))} rows={3} /></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submit} disabled={saving || !form.client_name.trim() || !form.transport_mode}>{saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>
    </ProductScreen>
  );
}
